/**
 * NoActiveGameUI component displays the game selection screen when no game is active.
 * Shows available games as tiles and handles game selection with keyboard/gamepad navigation.
 * In a managed room, a mate who is not a host sees only a note to wait for the host.
 * In a curated room, curated games come first; the owner gets a Show all games
 * toggle after them, per-tile actions to add and remove games, and can drag
 * the curated games to reorder them.
 */
import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { GameTile, type GameTileAction } from '../GameTile/GameTile'
import { CuratedTileRow } from '../CuratedTileRow/CuratedTileRow'
import { playAreaTestIds } from '../PlayArea/playArea.testIds'
import { useSignals } from '@preact/signals-react/runtime'
import type { RoomViewVMNoActiveGameTileVM, RoomViewVMNoActiveGameVM } from '../../../deps/ui/playAreaVM/playAreaVM.types'
import { DevelopmentStatusBadge } from '../../development/DevelopmentStatus/DevelopmentStatus'
import { CloseIcon } from '../../library/Icons/CloseIcon'
import { PlusIcon } from '../../library/Icons/PlusIcon'

interface NoActiveGameUIProps {
  noActiveGame: RoomViewVMNoActiveGameVM
}

/** The owner's editing actions for a tile in a curated room. */
function curationActions(game: RoomViewVMNoActiveGameTileVM, actions: RoomViewVMNoActiveGameVM['actions']): GameTileAction[] {
  const curation = game.curation
  if (!curation) return []
  const id = game.gameId
  const testId = playAreaTestIds.getCurationActionTestId
  if (curation.placement === 'available') {
    return [{ label: 'Add to room', icon: <PlusIcon width={18} height={18} />, onClick: () => actions.addCuratedGame(id), testId: testId('add', id) }]
  }
  return [{ label: 'Remove from room', icon: <CloseIcon width={18} height={18} />, onClick: () => actions.removeCuratedGame(id), testId: testId('remove', id) }]
}

export function NoActiveGameUI({ noActiveGame }: NoActiveGameUIProps) {
  useSignals()

  const games = noActiveGame.games.value
  const focusedIndex = noActiveGame.focusedIndex.value
  const isActive = noActiveGame.focusNavigation.isActive.value
  const focusArea = noActiveGame.focusNavigation.focusArea.value
  const gameNotice = noActiveGame.gameNotice.value
  const waitingMessage = noActiveGame.waitingMessage.value
  const isCurated = noActiveGame.curation.isCurated.value
  const canEditCuration = noActiveGame.curation.canEdit.value
  const showAll = noActiveGame.curation.showAll.value
  const curatedCount = noActiveGame.curation.curatedCount.value

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const tileRefs = React.useRef<(HTMLDivElement | null)[]>([])

  // Measure the rendered rows instead of estimating from window width. The
  // tiles use flex-wrap, so viewport width alone is not a reliable proxy. The
  // widest row counts: a curated room's first section may hold a single game.
  React.useEffect(() => {
    function updateGamesPerRow(): void {
      const tiles = tileRefs.current.filter((tile): tile is HTMLDivElement => Boolean(tile))
      const tilesPerRowTop = new Map<number, number>()
      for (const tile of tiles) {
        const rowTop = Math.round(tile.offsetTop / 8)
        tilesPerRowTop.set(rowTop, (tilesPerRowTop.get(rowTop) ?? 0) + 1)
      }
      noActiveGame.actions.setGamesPerRow(Math.max(1, ...tilesPerRowTop.values()))
    }

    updateGamesPerRow()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      updateGamesPerRow()
    })

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [games, noActiveGame.actions])

  // Focus the tile when index changes
  React.useEffect(() => {
    if (isActive && focusArea === 'content' && tileRefs.current[focusedIndex]) {
      tileRefs.current[focusedIndex]?.focus()
    }
  }, [isActive, focusArea, focusedIndex])

  if (waitingMessage) {
    return (
      <div className="h-full w-full flex items-center justify-center p-8" data-testid={playAreaTestIds.waitingForHost}>
        <p className="text-lg text-muted" role="status">{waitingMessage}</p>
      </div>
    )
  }

  const renderTile = (game: RoomViewVMNoActiveGameTileVM, index: number) => {
    const disabled = game.disabled
    const note = game.disabledNote
    return (
      <GameTile
        key={game.gameId}
        ref={(el) => {
          tileRefs.current[index] = el
        }}
        displayName={game.displayName}
        publisherSlug={game.publisherSlug}
        description={game.description}
        disabled={disabled}
        disabledNote={note}
        badge={game.developmentProgress ? (
          <DevelopmentStatusBadge progress={game.developmentProgress} />
        ) : game.launchChannel ? (
          <span className="rounded-full bg-background/90 px-2 py-1 text-[10px] font-semibold text-foreground shadow-sm">Preview</span>
        ) : undefined}
        onDisabledSelect={game.incompatibleApiVersion ? () => noActiveGame.actions.showIncompatibleGameDetails(game.gameId) : undefined}
        thumbnail={
          game.thumbnailUrl ? (
            <img
              src={game.thumbnailUrl}
              alt={`${game.displayName} thumbnail`}
              draggable={false}
              className="w-full h-full object-cover"
            />
          ) : undefined
        }
        onSelect={() => noActiveGame.actions.selectGame(game.gameId)}
        onConfigure={game.hasConfigure ? (() => noActiveGame.actions.configureGame(game.gameId)) : undefined}
        data-testid={playAreaTestIds.getStartGameButtonTestId(game.gameId)}
        tabIndex={
          disabled
            ? -1
            : isActive && focusArea === 'content' && index === focusedIndex
              ? 0
              : -1
        }
        isFocused={disabled ? false : (isActive && focusArea === 'content' && index === focusedIndex)}
        actions={curationActions(game, noActiveGame.actions)}
      />
    )
  }

  const curatedGames = isCurated ? games.slice(0, curatedCount) : []
  const otherGames = isCurated ? games.slice(curatedCount) : games
  const tileRow = 'flex flex-wrap content-start gap-6 justify-start'

  return (
    <div ref={containerRef} className="h-full w-full overflow-y-auto flex flex-col gap-6 p-8">
      {isCurated && canEditCuration && curatedGames.length > 0 && (
        <CuratedTileRow
          items={curatedGames}
          renderTile={renderTile}
          onMove={noActiveGame.actions.moveCuratedGame}
          className={tileRow}
        />
      )}
      {isCurated && !(canEditCuration && curatedGames.length > 0) && (
        <div className={tileRow}>
          {curatedGames.map((game, index) => renderTile(game, index))}
          {curatedGames.length === 0 && (
            <p className="text-muted" role="status">
              {canEditCuration ? 'No games picked yet. Show all games to add some to this room.' : 'No games have been picked for this room yet.'}
            </p>
          )}
        </div>
      )}
      {isCurated && canEditCuration && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={noActiveGame.actions.toggleShowAllGames}
            aria-expanded={showAll}
            data-testid={playAreaTestIds.showAllGamesToggle}
            className="rounded-md border border-basic px-3 py-1.5 text-sm font-medium text-primary hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
          >
            {showAll ? 'Hide other games' : 'Show all games'}
          </button>
          {curatedGames.length > 1 && <span className="text-sm text-muted">Drag games to change their order.</span>}
        </div>
      )}
      {otherGames.length > 0 && (
        <div className={tileRow}>{otherGames.map((game, index) => renderTile(game, curatedGames.length + index))}</div>
      )}
      <Dialog.Root open={Boolean(gameNotice)} onOpenChange={(open) => (open ? undefined : noActiveGame.actions.closeGameNotice())}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-overlay z-[10000]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-[10001] w-[min(90vw,28rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-basic bg-background p-6 shadow-xl focus:outline-none">
            <Dialog.Title className="text-lg font-bold text-primary">{gameNotice?.title}</Dialog.Title>
            <Dialog.Description className="mt-3 text-sm text-muted leading-relaxed">
              {gameNotice?.message}
            </Dialog.Description>
            <div className="mt-6 flex justify-end">
              <Dialog.Close asChild>
                <button type="button" className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-inverse hover:opacity-90">
                  Close
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
